import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import AssignOwnerDialog from "@/components/AssignOwnerDialog";
import {
  Package, ClipboardCheck, ArrowRight, User, Building2, AlertCircle,
  AlertTriangle, Timer, TrendingUp, CheckCircle2, XCircle, Flame, UserPlus,
  ChevronRight, Bell, Trash2,
  MessageSquare,
} from "lucide-react";

// Phase 3: Pipeline Dashboard. Extends the Phase 1 Kanban with:
//  • Metric cards (Active / Overdue / This Month Signed / Installed / Avg Lead Time)
//  • Filter bar (Agency, Signing Agent, Priority, Overdue-only)
//  • Per-card aging pill (days-in-stage + SLA colour)
//  • Top-agencies leaderboard rail
// Legal forward transitions triggered by drag-drop. Only these pairs will
// call an endpoint on drop; everything else no-ops with a toast pointing to
// the detail page (some transitions need extra input like a reason or a
// complete checklist and shouldn't be drag-completable).
const DRAG_TRANSITIONS = {
  "received:in_production":       { endpoint: "take-production",    perm: "onboarding.production.manage" },
  "in_production:testing":        { endpoint: "send-to-testing",    perm: "onboarding.production.manage" },
  "testing:agent_review":         { endpoint: "testing-complete",   perm: "onboarding.testing.manage" },
  "dealer_install:install_complete": { endpoint: "install-complete", perm: null /* signing agent or admin */ },
  "testing:in_production":        { endpoint: "return-to-production", perm: "onboarding.testing.manage", needsReason: true },
};

const STAGE_META = [
  { key: "pending_program_selection", label: "Pending Program Selection", tone: "bg-[#F1F3F5] border-[#E9ECEF] text-[#6C757D]", accent: "bg-[#6C757D]" },
  { key: "received",                  label: "Received",                  tone: "bg-blue-50 border-blue-200 text-blue-700",      accent: "bg-blue-500" },
  { key: "in_production",             label: "In Production",             tone: "bg-orange-50 border-orange-200 text-orange-700", accent: "bg-orange-500" },
  { key: "testing",                   label: "Testing",                   tone: "bg-purple-50 border-purple-200 text-purple-700", accent: "bg-purple-500" },
  { key: "agent_review",              label: "Agent Review",              tone: "bg-[#FFF8E1] border-[#D4AF37]/60 text-[#8A6D1F]", accent: "bg-[#D4AF37]" },
  { key: "dealer_install",            label: "Dealer Install",            tone: "bg-yellow-50 border-yellow-200 text-yellow-800", accent: "bg-yellow-500" },
  { key: "install_complete",          label: "Install Complete",          tone: "bg-green-50 border-green-200 text-green-700",   accent: "bg-green-500" },
];

const SLA_PILL = {
  ok:       { cls: "bg-[#F1F3F5] text-[#495057] border-[#DEE2E6]", label: "On track" },
  warn:     { cls: "bg-amber-50 text-amber-700 border-amber-300",   label: "Watch" },
  overdue:  { cls: "bg-red-50 text-red-700 border-red-300",         label: "Overdue" },
  terminal: { cls: "bg-green-50 text-green-700 border-green-200",   label: "Done" },
};

export default function OnboardingBoard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialOverdue = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("overdue") === "1";
  const [filters, setFilters] = useState({ agency: "", signing_agent_id: "", priority: "", overdue_only: initialOverdue });
  const [assignTarget, setAssignTarget] = useState(null); // {record, defaultRole}
  const [dragOver, setDragOver] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [nudging, setNudging] = useState(false);
  // Admin escape-hatch dialog for cross-stage drags (any-to-any move).
  const [moveDialog, setMoveDialog] = useState(null); // {rec, toStage}

  const nudgeOverdue = async () => {
    const overdue = metrics?.totals?.overdue || 0;
    if (overdue === 0) return;
    const message = window.prompt(
      `This will send an in-app nudge to the signing agent (and any assigned owner) of every overdue record — ${overdue} record(s).\n\nOptional personal note (leave blank for the default message):`,
      "",
    );
    if (message === null) return;
    setNudging(true);
    try {
      const { data } = await api.post("/admin/onboarding/nudge-overdue", { message: message || "" });
      toast.success(`Nudged ${data.records_touched} record(s) · ${data.notifications_sent} notification(s) sent`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nudge failed");
    } finally { setNudging(false); }
  };

  const hasPerm = useCallback((p) => user?.role === "admin" || (user?.permissions || []).includes(p), [user]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.agency) params.agency = filters.agency;
      if (filters.signing_agent_id) params.signing_agent_id = filters.signing_agent_id;
      if (filters.priority) params.priority = filters.priority;
      if (filters.overdue_only) params.overdue_only = true;
      const [listRes, metricsRes] = await Promise.all([
        api.get("/onboarding", { params }),
        api.get("/onboarding/metrics"),
      ]);
      setData(listRes.data);
      setMetrics(metricsRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load onboarding");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const claimRecord = async (rec) => {
    if (rec.assigned_production_user_name && !window.confirm(`Take over "${rec.dealer_name || "this dealer"}" from ${rec.assigned_production_user_name}?`)) return;
    try {
      const { data } = await api.post(`/onboarding/${rec.id}/claim-ownership`);
      toast.success(data.previous_owner ? `You took over from ${data.previous_owner}` : "You now own this dealer set-up");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not claim ownership");
    }
  };

  const deleteRecord = async (rec) => {
    if (!window.confirm(`Delete onboarding for "${rec.dealer_name || 'this dealer'}"? This hard-deletes the record + activity log + testing/correction rows. Cannot be undone (use to clear demo/training content).`)) return;
    try {
      await api.delete(`/onboarding/${rec.id}`);
      toast.success(`Deleted onboarding · ${rec.dealer_name || rec.id}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const total = useMemo(() => (data?.records || []).length, [data]);
  const [ownerFilter, setOwnerFilter] = useState("all");
  const hasFilters = filters.agency || filters.signing_agent_id || filters.priority || filters.overdue_only;

  // Drag-drop stage transition. Native HTML5 DnD (no extra deps).
  // - Admins can drag ANY record to ANY stage — the drop opens a reason +
  //   keep/reset dialog and calls the admin `/move-stage` escape hatch.
  // - Non-admins are limited to the DRAG_TRANSITIONS allow-list (permission
  //   gated, calls the canonical forward endpoints).
  const isAdmin = user?.role === "admin";
  const canDrag = (rec) => {
    if (isAdmin) return true;
    return Object.keys(DRAG_TRANSITIONS).some((k) => k.startsWith(rec.status + ":"))
      && (hasPerm(DRAG_TRANSITIONS[Object.keys(DRAG_TRANSITIONS).find((k) => k.startsWith(rec.status + ":"))]?.perm)
          || DRAG_TRANSITIONS[Object.keys(DRAG_TRANSITIONS).find((k) => k.startsWith(rec.status + ":"))]?.perm == null);
  };

  const handleDrop = async (targetStage, recOverride = null) => {
    const rec = recOverride || dragging;
    setDragOver(null); setDragging(null);
    if (!rec || rec.status === targetStage) return;
    const key = `${rec.status}:${targetStage}`;
    const t = DRAG_TRANSITIONS[key];
    // Super admin: every drag opens the reason + keep/reset dialog and uses the
    // admin `/move-stage` endpoint, so gated steps (e.g. testing checklist) can be overridden.
    if (isAdmin) {
      setMoveDialog({ rec, toStage: targetStage });
      return;
    }
    if (!t) {
      toast.error(`Can't drag ${rec.status.replace(/_/g,' ')} → ${targetStage.replace(/_/g,' ')}. Open the record to advance it.`);
      return;
    }
    if (t.perm && !hasPerm(t.perm)) {
      toast.error(`Missing permission: ${t.perm}`);
      return;
    }
    let body = null;
    if (t.needsReason) {
      const reason = window.prompt("Why are you returning this record to production?");
      if (!reason) return;
      body = { reason };
    }
    try {
      await api.post(`/onboarding/${rec.id}/${t.endpoint}`, body);
      toast.success(`Moved ${rec.dealer_name} → ${targetStage.replace(/_/g,' ')}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Transition failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 py-8" data-testid="onboarding-board-page">
        <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#D4AF37]">Operations</div>
            <h1 className="font-display text-4xl sm:text-5xl font-black text-[#0B132B] leading-none mt-1">Dealer Onboarding</h1>
            <p className="text-sm text-[#6C757D] mt-2 max-w-xl">
              Every dealer from executed agreement through install. Aging, SLA, and load-balancing signals in real time.
            </p>
          </div>
        </div>

        <MetricsRow metrics={metrics} loading={loading && !metrics} />

        <FilterBar
          facets={data?.facets}
          filters={filters}
          onChange={setFilters}
          totalVisible={total}
          overdueTotal={metrics?.totals?.overdue || 0}
          isAdmin={user?.role === "admin"}
          onNudge={nudgeOverdue}
          nudging={nudging}
        />

        {loading ? (
          <div className="text-sm text-[#6C757D] py-16">Loading onboarding records…</div>
        ) : total === 0 ? (
          <Card className="border-dashed border-2 border-[#E9ECEF] bg-white p-14 text-center rounded-sm" data-testid="onboarding-empty">
            <ClipboardCheck className="mx-auto text-[#6C757D]" size={28} />
            <h3 className="font-display text-xl font-bold text-[#0B132B] mt-3">
              {hasFilters ? "No records match those filters" : "No dealers in onboarding yet"}
            </h3>
            <p className="text-sm text-[#6C757D] mt-2">
              {hasFilters ? "Clear filters or widen the scope to see more." : "Records auto-create the moment a dealer signs their agreement."}
            </p>
            {hasFilters && (
              <Button data-testid="clear-filters-empty" variant="outline" size="sm"
                      className="mt-4 rounded-sm"
                      onClick={() => setFilters({ agency: "", signing_agent_id: "", priority: "", overdue_only: false })}>
                Clear filters
              </Button>
            )}
          </Card>
        ) : (
          <div>
            {/* Stage-flow rail — visualizes the pipeline direction so it's
                obvious that cards flow left-to-right. Legend acts as sticky
                header above the horizontally-scrolling Kanban. */}
            <div className="hidden lg:flex items-center gap-2 mb-3 px-1" data-testid="stage-flow-rail">
              {STAGE_META.map((s, i) => (
                <React.Fragment key={s.key}>
                  <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] font-bold px-2 py-1 rounded-sm border ${s.tone}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${s.accent}`} />
                    {s.label}
                  </div>
                  {i < STAGE_META.length - 1 && <ChevronRight size={14} className="text-[#CED4DA] shrink-0" />}
                </React.Fragment>
              ))}
            </div>

            {(user?.role === "admin" || user?.role === "support" || (user?.permissions || []).includes("onboarding.production.manage")) && (
              <div className="flex items-center gap-1 mb-3" data-testid="owner-filter">
                <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#6C757D] mr-1">Show</span>
                {[["mine", "Mine"], ["unassigned", "Unassigned"], ["all", "All"]].map(([k, l]) => (
                  <button key={k} type="button" onClick={() => setOwnerFilter(k)}
                    className={`px-3 py-1 rounded-sm border text-xs font-semibold transition-colors ${ownerFilter === k ? "bg-[#0B132B] border-[#0B132B] text-white" : "bg-white border-[#E9ECEF] text-[#6C757D] hover:border-[#0B132B]"}`}
                    data-testid={`owner-filter-${k}`}>{l}</button>
                ))}
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr),300px] gap-4">
              <div className="overflow-x-auto pb-2 min-w-0" data-testid="onboarding-kanban-scroll">
                <div className="text-[10px] text-[#6C757D] mb-1.5 [@media(pointer:fine)]:hidden" data-testid="touch-drag-hint">Press and hold a dealer card, then drag it to another stage.</div>
                <div className="flex items-stretch gap-3 min-w-max" data-testid="onboarding-kanban">
                  {STAGE_META.map((stage, idx) => {
                    const cards = (data?.grouped?.[stage.key] || []).filter((c) =>
                      ownerFilter === "all" ? true :
                      ownerFilter === "mine" ? c.assigned_production_user_id === user?.id :
                      !c.assigned_production_user_id);
                    const avg = metrics?.avg_days_by_stage?.[stage.key];
                    const isDropTarget = dragging && dragging.status !== stage.key && (isAdmin || !!DRAG_TRANSITIONS[`${dragging.status}:${stage.key}`]);
                    const isDragOver = dragOver === stage.key;
                    return (
                      <React.Fragment key={stage.key}>
                        <div
                          className={`w-[248px] shrink-0 border-2 rounded-sm transition-all ${stage.tone} ${
                            isDropTarget ? "ring-2 ring-[#D4AF37] ring-offset-1" : ""
                          } ${isDragOver ? "bg-[#D4AF37]/10 border-[#D4AF37]" : ""}`}
                          data-testid={`onboarding-column-${stage.key}`}
                          onDragOver={(e) => { if (isDropTarget) { e.preventDefault(); setDragOver(stage.key); } }}
                          onDragLeave={() => setDragOver((k) => (k === stage.key ? null : k))}
                          onDrop={(e) => { e.preventDefault(); handleDrop(stage.key); }}
                        >
                          <div className="px-3 py-2 border-b-2 border-inherit flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${stage.accent}`} />
                            <div className="text-[10px] uppercase tracking-[0.16em] font-bold">{stage.label}</div>
                            <div className="ml-auto flex items-center gap-2">
                              {avg !== undefined && cards.length > 0 && (
                                <span className="text-[9px] font-bold opacity-70 flex items-center gap-0.5" title="Average days in stage">
                                  <Timer size={9} /> {avg}d
                                </span>
                              )}
                              <span className="text-[10px] font-bold bg-white/70 px-1.5 rounded-sm">{cards.length}</span>
                            </div>
                          </div>
                          <div className="p-2 space-y-2 min-h-[220px]">
                            {cards.length === 0 ? (
                              <div className="text-[11px] italic text-[#6C757D] px-1 py-6 text-center">
                                {isDropTarget ? "Drop to move here" : "Empty"}
                              </div>
                            ) : cards.map((c) => (
                              <OnboardingCard
                                key={c.id}
                                rec={c}
                                user={user}
                                canDrag={canDrag(c)}
                                onDragStart={() => setDragging(c)}
                                onDragEnd={() => { setDragging(null); setDragOver(null); }}
                                onTouchOver={(k) => setDragOver(k)}
                                onTouchDrop={(k) => (k ? handleDrop(k, c) : (setDragging(null), setDragOver(null)))}
                                onAssign={(role) => setAssignTarget({ record: c, defaultRole: role })}
                                onClaim={claimRecord}
                                onDelete={user?.role === "admin" ? () => deleteRecord(c) : null}
                              />
                            ))}
                          </div>
                        </div>
                        {idx < STAGE_META.length - 1 && (
                          <div className="hidden lg:flex items-center shrink-0" aria-hidden="true">
                            <ChevronRight size={18} className="text-[#CED4DA]" />
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
              <SidebarRail metrics={metrics} />
            </div>
          </div>
        )}
      </main>
      {assignTarget && (
        <AssignOwnerDialog
          record={assignTarget.record}
          defaultRole={assignTarget.defaultRole}
          onClose={() => setAssignTarget(null)}
          onSaved={load}
        />
      )}
      {moveDialog && (
        <MoveStageDialog
          rec={moveDialog.rec}
          toStage={moveDialog.toStage}
          onClose={() => setMoveDialog(null)}
          onSaved={() => { setMoveDialog(null); load(); }}
        />
      )}
    </div>
  );
}

// Admin-only "move to any stage" dialog — captures a required reason and
// asks whether downstream data should be kept or reset.
function MoveStageDialog({ rec, toStage, onClose, onSaved }) {
  const [reason, setReason] = useState("");
  const [mode, setMode] = useState("keep");
  const [busy, setBusy] = useState(false);
  const stageLabel = (STAGE_META.find((s) => s.key === toStage) || {}).label || toStage;
  const fromLabel = (STAGE_META.find((s) => s.key === rec.status) || {}).label || rec.status;

  const submit = async () => {
    if (!reason.trim()) { toast.error("Reason is required"); return; }
    setBusy(true);
    try {
      await api.post(`/onboarding/${rec.id}/move-stage`, {
        to_stage: toStage,
        reason: reason.trim(),
        mode,
      });
      toast.success(`${rec.dealer_name} → ${stageLabel} (${mode})`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Move failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="move-stage-dialog">
      <div className="bg-white rounded-sm border border-[#E9ECEF] p-6 max-w-md w-full shadow-2xl">
        <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#D4AF37] mb-1">Admin stage move</div>
        <h3 className="text-lg font-bold text-[#0B132B] mb-1">{rec.dealer_name || "Onboarding"}</h3>
        <div className="text-sm text-[#6C757D] mb-4">
          Move from <b className="text-[#0B132B]">{fromLabel}</b> → <b className="text-[#0B132B]">{stageLabel}</b>
        </div>
        <label className="text-xs font-bold text-[#0B132B] uppercase tracking-wider">Reason *</label>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
          placeholder="Why is this dealer moving stages?"
          className="w-full mt-1 rounded-sm border border-[#E9ECEF] p-2 text-sm"
          data-testid="move-stage-reason" />
        <div className="mt-4 space-y-2">
          <label className={`flex items-start gap-2 cursor-pointer p-2.5 border rounded-sm ${mode === "keep" ? "border-[#0B132B] bg-[#0B132B]/[0.03]" : "border-[#E9ECEF]"}`}>
            <input type="radio" name="mv-mode" value="keep" checked={mode === "keep"}
              onChange={() => setMode("keep")} className="mt-0.5 accent-[#0B132B]"
              data-testid="move-stage-mode-keep" />
            <div>
              <div className="text-xs font-bold text-[#0B132B]">Save current data</div>
              <div className="text-[11px] text-[#6C757D] leading-snug mt-0.5">
                Only the status changes. Existing assignments, timestamps, and install checklist stay in place — resume where you left off.
              </div>
            </div>
          </label>
          <label className={`flex items-start gap-2 cursor-pointer p-2.5 border rounded-sm ${mode === "reset" ? "border-red-500 bg-red-50" : "border-[#E9ECEF]"}`}>
            <input type="radio" name="mv-mode" value="reset" checked={mode === "reset"}
              onChange={() => setMode("reset")} className="mt-0.5 accent-red-500"
              data-testid="move-stage-mode-reset" />
            <div>
              <div className="text-xs font-bold text-[#0B132B]">Reset and start from this stage</div>
              <div className="text-[11px] text-[#6C757D] leading-snug mt-0.5">
                Wipe every downstream field (assignments, completion timestamps, install statuses). Use when the dealer is legitimately restarting from here.
              </div>
            </div>
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="outline" size="sm" onClick={onClose} className="rounded-sm h-9" data-testid="move-stage-cancel">
            Cancel
          </Button>
          <Button size="sm" onClick={submit} disabled={busy}
            className={`rounded-sm h-9 text-white ${mode === "reset" ? "bg-red-600 hover:bg-red-700" : "bg-[#0B132B] hover:bg-[#1C2541]"}`}
            data-testid="move-stage-submit">
            {busy ? "Moving…" : mode === "reset" ? "Move + Reset" : "Move (keep)"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---- Metric Cards ---------------------------------------------------------
function MetricsRow({ metrics, loading }) {
  const t = metrics?.totals || {};
  const cards = [
    { key: "active",    label: "Active Pipeline", value: t.active ?? "—", icon: <ClipboardCheck size={14} />, tone: "text-[#0B132B]" },
    { key: "overdue",   label: "Overdue SLA",     value: t.overdue ?? 0, icon: <AlertTriangle size={14} />,   tone: (t.overdue || 0) > 0 ? "text-red-600" : "text-[#0B132B]" },
    { key: "watch",     label: "Watch",           value: t.warn ?? 0,    icon: <Flame size={14} />,           tone: (t.warn || 0) > 0 ? "text-amber-600" : "text-[#0B132B]" },
    { key: "signed",    label: "Signed / MTD",    value: t.this_month_signed ?? 0, icon: <TrendingUp size={14} />, tone: "text-[#0B132B]" },
    { key: "installed", label: "Installed / MTD", value: t.this_month_installed ?? 0, icon: <CheckCircle2 size={14} />, tone: "text-green-700" },
    { key: "lead",      label: "Avg Lead Time",   value: metrics?.avg_lead_time_days != null ? `${metrics.avg_lead_time_days}d` : "—", icon: <Timer size={14} />, tone: "text-[#0B132B]" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-5" data-testid="onboarding-metrics-row">
      {cards.map((c) => (
        <Card key={c.key} className="bg-white border border-[#E9ECEF] rounded-sm p-3" data-testid={`metric-${c.key}`}>
          <div className="text-[9px] uppercase tracking-[0.16em] font-bold text-[#6C757D] flex items-center gap-1">
            {c.icon} {c.label}
          </div>
          <div className={`font-display text-3xl font-black mt-1 ${c.tone}`}>
            {loading ? "…" : c.value}
          </div>
        </Card>
      ))}
    </div>
  );
}

// ---- Filter bar -----------------------------------------------------------
function FilterBar({ facets, filters, onChange, totalVisible, overdueTotal, isAdmin, onNudge, nudging }) {
  const set = (patch) => onChange({ ...filters, ...patch });
  const clear = () => onChange({ agency: "", signing_agent_id: "", priority: "", overdue_only: false });
  const active = filters.agency || filters.signing_agent_id || filters.priority || filters.overdue_only;
  return (
    <div className="bg-white border border-[#E9ECEF] rounded-sm p-3 mb-4 flex items-center gap-3 flex-wrap" data-testid="onboarding-filter-bar">
      <select
        data-testid="filter-agency"
        value={filters.agency}
        onChange={(e) => set({ agency: e.target.value })}
        className="text-xs rounded-sm border border-[#E9ECEF] bg-white px-2 py-1.5 min-w-[140px]"
      >
        <option value="">All agencies</option>
        {(facets?.agencies || []).map((a) => <option key={a} value={a}>{a}</option>)}
      </select>
      <select
        data-testid="filter-agent"
        value={filters.signing_agent_id}
        onChange={(e) => set({ signing_agent_id: e.target.value })}
        className="text-xs rounded-sm border border-[#E9ECEF] bg-white px-2 py-1.5 min-w-[140px]"
      >
        <option value="">All signing agents</option>
        {(facets?.agents || []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
      </select>
      <select
        data-testid="filter-priority"
        value={filters.priority}
        onChange={(e) => set({ priority: e.target.value })}
        className="text-xs rounded-sm border border-[#E9ECEF] bg-white px-2 py-1.5"
      >
        <option value="">All priority</option>
        <option value="high">High</option>
        <option value="normal">Normal</option>
        <option value="low">Low</option>
      </select>
      <label className="flex items-center gap-2 text-xs font-bold text-[#0B132B] cursor-pointer" data-testid="filter-overdue-label">
        <input
          type="checkbox"
          data-testid="filter-overdue"
          checked={filters.overdue_only}
          onChange={(e) => set({ overdue_only: e.target.checked })}
          className="rounded-sm"
        />
        Overdue only
        {overdueTotal > 0 && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-red-100 text-red-700 font-bold">{overdueTotal}</span>
        )}
      </label>
      <div className="ml-auto flex items-center gap-3 text-[11px] text-[#6C757D]">
        <span data-testid="filter-total">Showing <b className="text-[#0B132B]">{totalVisible}</b></span>
        {isAdmin && overdueTotal > 0 && (
          <Button
            data-testid="filter-nudge-overdue" size="sm" variant="outline"
            className="h-7 rounded-sm text-[10px] uppercase tracking-[0.12em] font-bold border-red-300 text-red-700 hover:bg-red-50"
            onClick={onNudge} disabled={nudging}
            title="Send an in-app nudge to the signing agent of every overdue record"
          >
            <Bell size={12} className="mr-1" /> {nudging ? "Sending…" : `Nudge ${overdueTotal} overdue`}
          </Button>
        )}
        {active && (
          <Button data-testid="filter-clear" size="sm" variant="outline"
                  className="h-7 rounded-sm text-[10px] uppercase tracking-[0.12em] font-bold"
                  onClick={clear}>
            <XCircle size={12} className="mr-1" /> Clear
          </Button>
        )}
      </div>
    </div>
  );
}

// ---- Sidebar leaderboard --------------------------------------------------
function SidebarRail({ metrics }) {
  const top = metrics?.top_agencies || [];
  const byStage = metrics?.counts_by_stage || {};
  return (
    <div className="space-y-4" data-testid="onboarding-sidebar-rail">
      <Card className="bg-white border border-[#E9ECEF] rounded-sm p-4">
        <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#6C757D] mb-2">Top Agencies</div>
        {top.length === 0 ? (
          <div className="text-xs italic text-[#6C757D]">No activity yet.</div>
        ) : (
          <div className="space-y-2">
            {top.map((a) => (
              <div key={a.agency} className="flex items-center gap-2 text-xs" data-testid={`top-agency-${a.agency}`}>
                <Building2 size={12} className="text-[#6C757D]" />
                <div className="flex-1 truncate font-medium text-[#0B132B]">{a.agency}</div>
                <div className="font-black text-[#0B132B]">{a.count}</div>
              </div>
            ))}
          </div>
        )}
      </Card>
      <Card className="bg-white border border-[#E9ECEF] rounded-sm p-4">
        <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#6C757D] mb-2">Stage Breakdown</div>
        <div className="space-y-2">
          {STAGE_META.map((s) => (
            <div key={s.key} className="flex items-center gap-2 text-xs">
              <span className={`w-2 h-2 rounded-full ${s.accent}`} />
              <div className="flex-1 truncate">{s.label}</div>
              <div className="font-black text-[#0B132B]">{byStage[s.key] ?? 0}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// ---- Card -----------------------------------------------------------------
// Touch (phone / tablet) drag: HTML5 DnD doesn't fire on touchscreens, so a long-press
// lifts the card, a ghost follows the finger, and the column under the finger on release wins.
function useTouchDrag(ref, handlers) {
  const h = React.useRef(handlers); h.current = handlers;
  const enabled = handlers.enabled;
  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;
    const onStart = () => h.current.onStart(), onOver = (k) => h.current.onOver(k), onDrop = (k) => h.current.onDrop(k);
    let timer = null, ghost = null, active = false, startX = 0, startY = 0, lastStage = null;
    const stageAt = (x, y) => document.elementFromPoint(x, y)?.closest("[data-testid^=onboarding-column-]")?.getAttribute("data-testid")?.replace("onboarding-column-", "") || null;
    const cleanup = () => { clearTimeout(timer); timer = null; if (ghost) { ghost.remove(); ghost = null; } if (active) { active = false; el.style.opacity = ""; document.body.style.overflow = ""; } };
    const onTouchStart = (e) => {
      if (e.touches.length !== 1 || e.target.closest("button,a")) return;
      startX = e.touches[0].clientX; startY = e.touches[0].clientY;
      timer = setTimeout(() => {
        active = true; onStart();
        if (navigator.vibrate) navigator.vibrate(30);
        const r = el.getBoundingClientRect();
        ghost = el.cloneNode(true);
        Object.assign(ghost.style, { position: "fixed", left: `${r.left}px`, top: `${r.top}px`, width: `${r.width}px`, pointerEvents: "none", opacity: "0.9", zIndex: 9999, transform: "rotate(2deg) scale(1.03)", boxShadow: "0 12px 30px rgba(0,0,0,.25)" });
        ghost.dataset.offX = String(startX - r.left); ghost.dataset.offY = String(startY - r.top);
        document.body.appendChild(ghost);
        el.style.opacity = "0.4"; document.body.style.overflow = "hidden";
      }, 350);
    };
    const onTouchMove = (e) => {
      const t = e.touches[0];
      if (!active) { if (Math.abs(t.clientX - startX) > 8 || Math.abs(t.clientY - startY) > 8) { clearTimeout(timer); timer = null; } return; }
      e.preventDefault();
      if (ghost) { ghost.style.left = `${t.clientX - Number(ghost.dataset.offX)}px`; ghost.style.top = `${t.clientY - Number(ghost.dataset.offY)}px`; }
      const sc = document.querySelector("[data-testid=onboarding-kanban-scroll]");
      if (sc) { const r = sc.getBoundingClientRect(); if (t.clientX > r.right - 60) sc.scrollLeft += 12; else if (t.clientX < r.left + 60) sc.scrollLeft -= 12; }
      const k = stageAt(t.clientX, t.clientY);
      if (k !== lastStage) { lastStage = k; onOver(k); }
    };
    const onTouchEnd = (e) => {
      if (!active) { cleanup(); return; }
      const t = e.changedTouches[0];
      const k = stageAt(t.clientX, t.clientY);
      cleanup(); onDrop(k);
    };
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd); el.addEventListener("touchcancel", () => { cleanup(); onDrop(null); });
    return () => { cleanup(); el.removeEventListener("touchstart", onTouchStart); el.removeEventListener("touchmove", onTouchMove); el.removeEventListener("touchend", onTouchEnd); };
  }, [ref, enabled]);
}

function OnboardingCard({ rec, user, onAssign, onClaim, canDrag, onDragStart, onDragEnd, onDelete, onTouchOver, onTouchDrop }) {
  const cardRef = React.useRef(null);
  useTouchDrag(cardRef, { enabled: !!canDrag, onStart: () => onDragStart?.(), onOver: (k) => onTouchOver?.(k), onDrop: (k) => onTouchDrop?.(k) });
  const canSetup =
    rec.status === "pending_program_selection" &&
    (user?.role === "admin" ||
     (user?.role === "agency_admin" && user?.agency === rec.agency) ||
     rec.signing_agent_id === user?.id);
  const canAssign = user?.role === "admin" || (user?.permissions || []).includes("onboarding.install.assign");
  const defaultAssignRole =
    rec.status === "received" || rec.status === "in_production" ? "production" :
    rec.status === "testing" ? "testing" :
    rec.status === "dealer_install" ? "install" : "production";
  const sla = SLA_PILL[rec.sla_status] || SLA_PILL.ok;
  const days = rec.days_in_stage;
  return (
    <Card
      ref={cardRef}
      className={`relative bg-white border border-[#E9ECEF] rounded-sm p-3 pl-4 overflow-hidden min-w-0 hover:shadow-md transition-shadow select-none ${canDrag ? "cursor-grab active:cursor-grabbing" : ""}`}
      style={canDrag ? { WebkitTouchCallout: "none" } : undefined}
      draggable={!!canDrag}
      onDragStart={(e) => { e.dataTransfer.setData("text/plain", rec.id); e.dataTransfer.effectAllowed = "move"; onDragStart?.(e); }}
      onDragEnd={onDragEnd}
      title={canDrag ? "Click and hold (or press and hold on touch) to drag to another stage" : undefined}
      data-testid={`onboarding-card-${rec.id}`}
    >
      {canDrag && <span className="absolute left-0 top-0 bottom-0 w-[6px] bg-[#D4AF37]/50 hover:bg-[#D4AF37]" aria-hidden="true" data-testid={`card-grip-${rec.id}`} />}
      <Link to={`/onboarding/${rec.id}`} className="block min-w-0" draggable={false}>
        <div className="flex items-start gap-1.5 min-w-0">
          <div className="text-sm font-bold text-[#0B132B] leading-snug line-clamp-2 break-words hover:text-[#D4AF37] transition-colors flex-1 min-w-0">
            {rec.dealer_name || "Untitled dealer"}
          </div>
          {rec.unread_messages > 0 && (
            <span title={`${rec.unread_messages} unread message(s)`} className="shrink-0 inline-flex items-center gap-0.5 px-1.5 h-[18px] rounded-full bg-red-600 text-white text-[10px] font-bold" data-testid={`card-unread-${rec.id}`}>
              <MessageSquare size={10} /> {rec.unread_messages}
            </span>
          )}
          {(rec.critical_flags || []).length > 0 && (
            <span title={`Missing before install: ${rec.critical_flags.join(", ")}`} className="shrink-0 inline-flex items-center gap-0.5 px-1.5 h-[18px] rounded-sm bg-red-50 border border-red-300 text-red-700 text-[10px] font-bold" data-testid={`card-flags-${rec.id}`}>
              <AlertTriangle size={10} /> {rec.critical_flags.length}
            </span>
          )}
          {rec.priority === "high" && (
            <span title="High priority" className="shrink-0 mt-0.5">
              <Flame size={12} className="text-red-500" />
            </span>
          )}
        </div>
      </Link>
      {rec.agency && (
        <div className="text-[10px] text-[#6C757D] flex items-center gap-1 mt-1 min-w-0"><Building2 size={10} className="shrink-0"/> <span className="truncate">{rec.agency}</span></div>
      )}
      {rec.signing_agent_name && (
        <div className="text-[10px] text-[#6C757D] flex items-center gap-1 mt-0.5 min-w-0"><User size={10} className="shrink-0"/> <span className="truncate">{rec.signing_agent_name}</span></div>
      )}
      {(user?.role === "admin" || user?.role === "support" || (user?.permissions || []).includes("onboarding.production.manage")) && (
        <div className="mt-1.5 flex items-center justify-between gap-2 text-[10px]" data-testid={`card-owner-${rec.id}`}>
          <span className="text-[#6C757D] truncate">Owner: <b className="text-[#0B132B]">{rec.assigned_production_user_name || "unassigned"}</b></span>
          {rec.assigned_production_user_id !== user?.id && (
            <button type="button" onClick={(e) => { e.preventDefault(); onClaim?.(rec); }}
              className="shrink-0 px-1.5 py-0.5 rounded-sm border border-[#D4AF37]/60 bg-[#D4AF37]/10 font-bold uppercase tracking-[0.1em] text-[9px] text-[#0B132B] hover:bg-[#D4AF37]/25"
              data-testid={`card-claim-${rec.id}`}>
              {rec.assigned_production_user_name ? "Take over" : "Claim"}
            </button>
          )}
        </div>
      )}
      {rec.eta?.ready_label && (
        <div className="mt-2 text-[10px] flex items-center gap-1 text-[#0B132B] min-w-0" data-testid={`onboarding-eta-${rec.id}`}>
          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${
            rec.eta.indicator === "delayed" ? "bg-red-500" :
            rec.eta.indicator === "at_risk" ? "bg-amber-500" :
            rec.eta.indicator === "ready_early" ? "bg-green-500" :
            rec.eta.indicator === "waiting_external" ? "bg-orange-400" : "bg-slate-400"
          }`} />
          <span className="font-bold whitespace-nowrap">ETA {rec.eta.ready_label}</span>
          {rec.eta.reason && <span className="text-[#6C757D] italic truncate min-w-0">· {rec.eta.reason}</span>}
        </div>
      )}
      <div className="flex items-center gap-1.5 flex-wrap mt-2" data-testid={`onboarding-card-meta-${rec.id}`}>
        {days != null && rec.sla_status !== "terminal" && (
          <span className={`text-[9px] px-1.5 py-0.5 rounded-sm border font-bold ${sla.cls}`} data-testid={`onboarding-aging-${rec.id}`}>
            <Timer size={9} className="inline mr-0.5" /> {days}d · {sla.label}
          </span>
        )}
        {rec.sla_status === "terminal" && (
          <span className={`text-[9px] px-1.5 py-0.5 rounded-sm border font-bold ${sla.cls}`}>
            <CheckCircle2 size={9} className="inline mr-0.5" /> {sla.label}
          </span>
        )}
        {(rec.products || []).length > 0 && (
          <span className="text-[9px] px-1.5 py-0.5 rounded-sm border border-[#E9ECEF] bg-[#F8F9FA] text-[#0B132B] font-bold">
            <Package size={9} className="inline mr-0.5" /> {rec.products.length}
          </span>
        )}
      </div>
      {canSetup && (
        <Link to={`/onboarding/${rec.id}/program-setup`} data-testid={`onboarding-setup-cta-${rec.id}`} draggable={false}>
          <Button size="sm" className="w-full mt-3 h-8 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white text-[11px] uppercase tracking-[0.14em] font-bold">
            Program Setup <ArrowRight size={10} className="ml-1.5"/>
          </Button>
        </Link>
      )}
      {rec.status === "pending_program_selection" && !canSetup && (
        <div className="mt-3 text-[10px] text-[#6C757D] flex items-center gap-1 italic">
          <AlertCircle size={10}/> Awaiting signing agent
        </div>
      )}
      {rec.status !== "pending_program_selection" && (
        <div className="mt-3 flex items-center gap-1 flex-wrap">
          <Link to={`/onboarding/${rec.id}`} data-testid={`onboarding-open-${rec.id}`} className="flex-1 min-w-[64px]" draggable={false}>
            <Button size="sm" variant="outline" className="w-full h-8 px-2 rounded-sm text-[11px] uppercase tracking-[0.14em] font-bold">
              Open <ArrowRight size={10} className="ml-1.5"/>
            </Button>
          </Link>
          {rec.dealer_id && (
            <Link to={`/dealers/${rec.dealer_id}/profile`} data-testid={`onboarding-profile-${rec.id}`} title="Dealer profile" draggable={false}>
              <Button size="sm" variant="outline" className="h-8 w-8 p-0 rounded-sm shrink-0"><Building2 size={12} /></Button>
            </Link>
          )}
          {canAssign && rec.status !== "install_complete" && (
            <Button
              size="sm"
              variant="outline"
              className="h-8 w-8 p-0 rounded-sm shrink-0"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onAssign(defaultAssignRole); }}
              data-testid={`onboarding-assign-${rec.id}`}
              title="Assign owner"
            >
              <UserPlus size={12} />
            </Button>
          )}
          {onDelete && (
            <Button
              size="sm"
              variant="outline"
              className="h-8 w-8 p-0 rounded-sm shrink-0 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(); }}
              data-testid={`onboarding-delete-${rec.id}`}
              title="Delete onboarding (super-admin — clears demo/training records)"
            >
              <Trash2 size={12} />
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}
